import type { WardrobeItem } from "@/components/Wardrobe";

/**
 * F30.5 — Versatility sibling card rendered immediately after the LB3 card.
 *
 * Three states:
 *  - empty  : guest, or auth with 0 matching wardrobe items
 *  - some   : auth with 1–4 matching items
 *  - strong : auth with 5+ matching items ("VERSATILITY · HIGH")
 *
 * Logic lifted from the deleted Section I of PurchaseConfidenceCard
 * (CATEGORY_PAIRS + getVersatilityCount).
 */

export interface VersatilityHeroItem {
  name?: string | null;
  category?: string | null;
}

const CATEGORY_PAIRS: Record<string, string[]> = {
  outerwear: ["top", "bottom", "dress", "shoes"],
  top: ["bottom", "shoes", "outerwear", "accessory"],
  bottom: ["top", "shoes", "outerwear", "accessory"],
  dress: ["shoes", "outerwear", "accessory", "bag"],
  shoes: ["top", "bottom", "dress", "outerwear"],
};

function getMatches(
  heroItem: VersatilityHeroItem,
  wardrobeItems: WardrobeItem[],
): { count: number; matchingItems: WardrobeItem[]; supported: boolean } {
  const heroCategory = (heroItem.category ?? "").toLowerCase();
  const compat = CATEGORY_PAIRS[heroCategory];
  if (!compat) return { count: 0, matchingItems: [], supported: false };
  const matchingItems = wardrobeItems.filter((i) =>
    i.category ? compat.includes(i.category.toLowerCase()) : false,
  );
  return { count: matchingItems.length, matchingItems, supported: true };
}

interface VersatilityCardProps {
  heroItem: VersatilityHeroItem | null;
  wardrobeItems: WardrobeItem[];
  isGuest: boolean;
}

const containerStyle: React.CSSProperties = {
  background: "#FFFCF8",
  border: "0.5px solid #E8E2D9",
  borderRadius: 20,
  margin: "0 0 12px",
  padding: 14,
};

function Swatch({
  item,
  index,
  size = 32,
}: {
  item: WardrobeItem;
  index: number;
  size?: number;
}) {
  const bg = item.image_url
    ? `url(${item.image_url}) center/cover`
    : item.color_hex ?? "#E0D8D0";
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        border: "2px solid #FFFCF8",
        background: bg,
        marginLeft: index === 0 ? 0 : -8,
        flexShrink: 0,
        display: "inline-block",
        boxShadow: "0 0 0 0.5px #E8E2D9",
      }}
    />
  );
}

function Eyebrow({ high }: { high: boolean }) {
  const color = high ? "#0F6E56" : "#9B958E";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 8,
      }}
    >
      <i
        className="ti ti-versions"
        style={{ fontSize: 14, color }}
        aria-hidden="true"
      />
      <span className="l2f-type-section-header" style={{ color }}>
        {high ? "VERSATILITY · HIGH" : "VERSATILITY"}
      </span>
    </div>
  );
}

export function VersatilityCard({
  heroItem,
  wardrobeItems,
  isGuest,
}: VersatilityCardProps) {
  if (!heroItem) return null;
  const { count, matchingItems, supported } = getMatches(
    heroItem,
    wardrobeItems,
  );
  // Hero category not in CATEGORY_PAIRS → don't render at all.
  if (!supported) return null;

  // ----- Empty state (guest, or auth with 0 matches) -----
  if (count === 0) {
    const body = isGuest
      ? "Sign up to see how versatile this piece is with your wardrobe."
      : "Add wardrobe items to see how versatile this piece is with what you own.";
    return (
      <section aria-label="Versatility" style={containerStyle}>
        <Eyebrow high={false} />
        <div
          className="l2f-type-caption"
          style={{ color: "#6B6560", lineHeight: 1.5 }}
        >
          {body}
        </div>
      </section>
    );
  }

  const high = count >= 5;
  const visible = matchingItems.slice(0, 4);
  const overflow = Math.max(0, count - visible.length);
  const names = matchingItems
    .slice(0, 4)
    .map((i) => i.name)
    .filter(Boolean)
    .join(", ");

  return (
    <section aria-label="Versatility" style={containerStyle}>
      <Eyebrow high={high} />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ display: "inline-flex", alignItems: "center" }}>
          {visible.map((item, idx) => (
            <Swatch key={item.id} item={item} index={idx} />
          ))}
          {high && overflow > 0 && (
            <span
              aria-hidden
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: "2px solid #FFFCF8",
                marginLeft: -8,
                background:
                  "linear-gradient(135deg, #C4A887 0%, #6B4F35 100%)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 0 0 0.5px #E8E2D9",
              }}
            >
              +{overflow}
            </span>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#2C2820",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Works with {count} item{count === 1 ? "" : "s"}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#6B6560",
              marginTop: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {high ? "A reliable piece in your wardrobe" : names}
          </div>
        </div>
      </div>
    </section>
  );
}

export default VersatilityCard;
